import { useEffect, useMemo, useState } from 'react'

import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type { BiddingIntradayOrder } from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import type { UpdateBiddingIntradayOrderRequest } from '@ebalance/proto/bffuser/v1/bidding_intraday_service_pb'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  HStack,
  Input,
  Text,
  TextArea,
  VStack,
  chakra,
} from '@enechain/ecloud-designsystem'

import {
  FieldErrorText,
  FieldLabel,
  FieldRoot,
} from '~/components/common/Field'
import { RequiredMark } from '~/components/common/RequiredMark'
import { TradeSideRadioGroup } from '~/components/common/TradeSideRadioGroup'
import { useBidLimits } from '~/hooks/bidding/useBidLimits'
import { resolveFormErrorAlertMessage } from '~/libs/formErrors'
import { convertAreaLabel } from '~/models/area'
import { type BidLimits, perBidVolumeLimitExceeded } from '~/models/bidLimits'
import { formatJstDateTimeHm, fromProtoDate } from '~/models/dateTime'
import { licenseTypeLabel } from '~/models/licenseType'
import {
  JEPX_PRICE_CONSTRAINT,
  JEPX_VOLUME_CONSTRAINT,
  isPlainDecimalInput,
  wrapNumberInputChange,
} from '~/models/numberInputConstraint'
import { IntradayFormulaFields } from '~/pages/IntradayBidding/components/IntradayFormulaFields'
import { IntradayPriceTypeRadio } from '~/pages/IntradayBidding/components/IntradayPriceTypeRadio'
import { useIntradayFeeAdjustedPricePreview } from '~/pages/IntradayBidding/hooks/useIntradayFeeAdjustedPricePreview'
import {
  type IntradayFeeEditSeed,
  useIntradayFeeEditBaseline,
} from '~/pages/IntradayBidding/hooks/useIntradayFeeEditBaseline'
import type { IntradayFeeStateHandle } from '~/pages/IntradayBidding/hooks/useIntradayFeeState'
import { useIntradayFeeTreatment } from '~/pages/IntradayBidding/hooks/useIntradayFeeTreatment'
import { useIntradayFormulaFields } from '~/pages/IntradayBidding/hooks/useIntradayFormulaFields'
import {
  type FeeAutoConsiderHint,
  type FeeAutoConsiderMatrixSetting,
  type FeeAutoConsiderPreview,
  type IntradayFeeEditBaseline,
  feeAdjustmentViolationMessage,
  feeAutoConsiderHint,
  toEditFeeHintKey,
  toFeeAutoConsiderMatrixSetting,
  usesDesiredPrice,
} from '~/pages/IntradayBidding/models/feeAutoConsider'
import { isMarketOrderRow } from '~/pages/IntradayBidding/models/intradayBidRow'
import {
  EMPTY_FORMULA_FIELD_VALUES,
  parseIntradayFormulaFormValues,
  toIntradayFormulaFieldValues,
} from '~/pages/IntradayBidding/models/intradayFormula'
import {
  buildUpdateIntradayOrderRequest,
  withdrawAtLocalOf,
} from '~/pages/IntradayBidding/models/intradayOrderWrite'
import { INTRADAY_P8_AVAILABLE } from '~/pages/IntradayBidding/models/intradayP8Availability'
import {
  type IntradayOrderEditFormInput,
  buildUpdateIntradayOrderFormSchema,
  isEditFormulaPrice,
} from '~/pages/IntradayBidding/models/schema'

type Props = {
  entity: BiddingIntradayOrder
  /** 下書き以外は表示専用 (入力欄をすべて非活性にする。 保存操作は呼び出し側が出さない)。 */
  readOnly: boolean
  /** 呼び出し側の保存ボタン (`form` 属性) と結び付ける form の id。 */
  formId: string
  /** GC 締切の表示文字列 (読み取り専用)。 */
  gcAtLabel: string
  /**
   * 保存を止めたい間 true (入札制限・手数料自動考慮の確認が済むまでと、 確認が必要な状態の間)。
   * 保存ボタンは本文の form の外 (呼び出し側) にあるので、 状態を呼び出し側へ渡す。
   */
  onSaveBlockedChange: (blocked: boolean) => void
  onValidSubmit: (request: UpdateBiddingIntradayOrderRequest) => void
  /** 項目脇に出ないエラーがある時だけ alert 文言を渡す (無ければ `undefined`)。 */
  onInvalidSubmit: (alertMessage: string | undefined) => void
  /**
   * 入札方式に戦略を選んでいるか (AC-BIN-005b)。 戦略の札の価格は段から導出するので、 その間の本文の
   * 価格は固定価格だけにする (固定価格 / フォーミュラの切替とフォーミュラ欄を出さない)。
   */
  isStrategyMode?: boolean
  /** 画面が開く前から取得している手数料自動考慮の状態 (開いた時に読む)。 */
  feeState: IntradayFeeStateHandle
}

const editFeeHintKey = (key: FeeAutoConsiderHint['key']): string =>
  `page:biddingIntraday.form.${toEditFeeHintKey(key)}`

const formValuesOf = (
  entity: BiddingIntradayOrder,
  feeBaseline: IntradayFeeEditBaseline,
  readOnly: boolean,
): IntradayOrderEditFormInput => {
  // AC-BIN-005: 保存済みのフォーミュラの入力値を初期値にする。 成行・表示専用の札は
  // フォーミュラを選べない (`formulaSelectable`) ので、 ここでは札の種類を見ない。
  const formula =
    entity.formula !== undefined
      ? toIntradayFormulaFieldValues(entity.formula)
      : undefined
  return {
    side: String(entity.direction),
    // 成行の "0" は指値 0 円ではない (`isMarketOrderRow`) ので入力欄には出さない。
    // 手数料自動考慮が ON の時は、 保存済みの入札価格から backend が逆算した希望価格を出す
    // (AC-BIN-004b・AC-BIN-007)。 フォーミュラの札も、 固定価格へ切り替えた時の初期値として持つ。
    priceYenPerKwh: isMarketOrderRow(entity)
      ? ''
      : feeBaseline.kind === 'fee'
        ? feeBaseline.initialDesiredPriceYenPerKwh
        : (entity.priceYenPerKwh?.value ?? ''),
    // フォーミュラは P8 の機能 (AC-BIN-133)。 `INTRADAY_P8_AVAILABLE` が false の間は選べないので、
    // 編集できる下書きは保存済みのフォーミュラの札も固定で開く (入札価格は保存時に計算した固定値
    // `entity.priceYenPerKwh`)。 表示専用は保存済みの種別を選んだ状態で出す (下に出すフォーミュラの入力値と
    // 選択肢を食い違わせない)。
    priceType:
      (readOnly || INTRADAY_P8_AVAILABLE) && formula !== undefined
        ? 'formula'
        : 'fixed',
    ...(formula ?? EMPTY_FORMULA_FIELD_VALUES),
    volumeMwh: entity.volumeMwh?.value ?? '',
    // 自動取消時刻の入力欄は **JST 壁時計**で読み書きする (同じ画面の GC・コマ帯と同じ軸)。
    withdrawAt: withdrawAtLocalOf(entity.withdrawAt),
    memoFree: entity.memoFree ?? '',
    memoJepx: entity.memoJepx ?? '',
  }
}

/** 開いた時点の札から、 手数料自動考慮の逆算を問い合わせる元を作る。 成行・価格なしは問い合わせない。 */
const feeEditSeedOf = (
  entity: BiddingIntradayOrder,
): IntradayFeeEditSeed | undefined => {
  const orderPrice = entity.priceYenPerKwh?.value
  return isMarketOrderRow(entity) || orderPrice === undefined
    ? undefined
    : { direction: entity.direction, orderPriceYenPerKwh: orderPrice }
}

/**
 * 時間前入札の編集フォーム (AC-BIN-007)。 下書きは編集でき、 それ以外は同じ項目を
 * 非活性で表示する (spec「入力欄をすべて非活性にし、保存操作を出さない」。 スポットの
 * `IndividualBidModal` と同じく native の `fieldset[disabled]` でまとめて止める)。
 *
 * どの版の entity を描くかはこの中で決める。 編集中はフォームを開いた時点の版に固定し
 * (フォームの初期値・承認モード・発注主体・楽観ロックの `expectedUpdatedAt` をそろえ、
 * 他の人の更新は保存時に Aborted で検知する)、 表示専用は版が変わるたびに作り直して
 * 最新の値を出す。
 */
export const IntradayOrderEditForm = (props: Props): React.ReactElement => (
  // 札ごとに作り直す: 手数料自動考慮の扱いと初期値は、 開いた時点の札で 1 回だけ決める (下の Shell)。
  <IntradayOrderEditFormShell key={props.entity.id} {...props} />
)

const IntradayOrderEditFormShell = (props: Props): React.ReactElement => {
  const { entity, readOnly, onSaveBlockedChange, feeState } = props
  const { t } = useTranslation()
  const version = `${String(entity.updatedAt?.seconds ?? '')}.${String(entity.updatedAt?.nanos ?? 0)}`
  // 入札制限 (AC-BIN-100 の上限数量(1札)): 本文が編集後の売/買の上限と照合する。 取得中は空の
  // lookup が上限なしと同じになって素通りするので、 取得が済むまで本文が保存を止める (作成モーダルと同じ)。
  const bidLimits = useBidLimits(BidSegment.INTRADAY)
  const feeSetting = toFeeAutoConsiderMatrixSetting({
    isLoading: bidLimits.isLoading,
    isError: bidLimits.isError,
    enabled: bidLimits.feeAutoConsiderEnabled,
  })
  const feeBaseline = useIntradayFeeEditBaseline(
    readOnly ? undefined : feeEditSeedOf(entity),
    feeState,
    feeSetting,
  )
  const isPending = feeBaseline.kind === 'pending'
  // 最初の応答が来るまでは入力欄を出さず、 保存も止める。
  useEffect(() => {
    if (!isPending) return undefined
    onSaveBlockedChange(true)
    return () => {
      onSaveBlockedChange(false)
    }
  }, [isPending, onSaveBlockedChange])
  if (isPending) {
    return (
      <Text color="Text.midEmphasis" fontSize="sm">
        {t('page:biddingIntraday.form.feeAutoConsiderLoading')}
      </Text>
    )
  }
  return (
    <IntradayOrderEditFormBody
      key={readOnly ? `${entity.id}:view:${version}` : `${entity.id}:edit`}
      {...props}
      bidLimits={bidLimits.limits}
      feeBaseline={feeBaseline}
      feeSetting={feeSetting}
      isBidLimitsLoading={bidLimits.isLoading}
    />
  )
}

const IntradayOrderEditFormBody = ({
  entity,
  readOnly,
  formId,
  gcAtLabel,
  feeBaseline,
  feeSetting,
  feeState,
  bidLimits,
  isBidLimitsLoading,
  onSaveBlockedChange,
  onValidSubmit,
  onInvalidSubmit,
  isStrategyMode = false,
}: Props & {
  feeBaseline: IntradayFeeEditBaseline
  feeSetting: FeeAutoConsiderMatrixSetting
  bidLimits: BidLimits
  isBidLimitsLoading: boolean
}): React.ReactElement => {
  const { t } = useTranslation()
  // 描画・検証・送信はすべてこの版から読む (版の切り替えは `IntradayOrderEditFormShell` が渡す key)。
  const [baseline] = useState(entity)
  const isMarketOrder = isMarketOrderRow(baseline)
  const fee = useIntradayFeeTreatment({
    isOpen: feeBaseline.kind !== 'none',
    opensAsFallbackOff: feeBaseline.kind === 'unavailable',
    setting: feeSetting,
    // 欄名・検証は入札制限の設定でなく開いた時点の扱いから作るので、 応答と突き合わせる相手もそれにする。
    basis: { status: 'success', enabled: feeBaseline.kind === 'fee' },
    feeState,
  })
  const isFeeFallbackOff = fee.treatment.status === 'fallbackOff'
  const isDesiredPrice = usesDesiredPrice(
    fee.treatment,
    feeBaseline.kind === 'fee',
  )
  const canChoosePriceType = !readOnly && !isMarketOrder
  // 固定価格とフォーミュラを切り替えられるのは、 編集できる指値の下書きだけ (AC-BIN-007)。 戦略の札の
  // 価格は段から決まるので、 戦略を選んでいる間の本文の価格は固定価格だけにする
  // (フォーミュラを送ると、 使われないのに保存時に評価され、 変数の値が無いと保存が拒否される)。
  const formulaSelectable = canChoosePriceType && !isStrategyMode
  const deliveryDate = useMemo(
    () =>
      baseline.targetDate !== undefined
        ? fromProtoDate(baseline.targetDate)
        : new Date(Number.NaN),
    [baseline],
  )

  const schema = useMemo(
    () =>
      buildUpdateIntradayOrderFormSchema({
        withdrawAtMessage: (violation): string => {
          switch (violation.kind) {
            case 'afterGc':
              return t('page:biddingIntraday.bidCreate.withdrawAtAfterGc', {
                gcAt: formatJstDateTimeHm(violation.gcAt),
              })
            case 'past':
              return t('page:biddingIntraday.bidCreate.withdrawAtPast')
          }
        },
        koma: baseline.timeCode,
        deliveryDate,
        isMarketOrder,
        feeAutoConsiderOn: isDesiredPrice,
        formulaSelectable,
      }),
    [
      t,
      baseline,
      deliveryDate,
      isMarketOrder,
      isDesiredPrice,
      formulaSelectable,
    ],
  )

  const {
    control,
    register,
    handleSubmit,
    setError,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<IntradayOrderEditFormInput>({
    resolver: zodResolver(schema),
    defaultValues: formValuesOf(baseline, feeBaseline, readOnly),
  })

  const priceReg = register('priceYenPerKwh')
  const volumeReg = register('volumeMwh')

  // 戦略から通常に戻すと、 入力済みのフォーミュラ欄がそのまま元の表示に戻る (form の値は保つ)。
  // 戦略は P8 の機能で、 `INTRADAY_P8_AVAILABLE` が false の間は選べない。
  const priceType = watch('priceType')
  const isFormula = isEditFormulaPrice(formulaSelectable, priceType)
  const usesFeePreview =
    canChoosePriceType &&
    !isFormula &&
    !isFeeFallbackOff &&
    (feeBaseline.kind === 'fee' || feeBaseline.kind === 'off')
  const [
    formulaVariable,
    formulaReferenceArea,
    formulaOperator,
    formulaOffset,
  ] = watch([
    'formulaVariable',
    'formulaReferenceArea',
    'formulaOperator',
    'formulaOffset',
  ])

  // AC-BIN-004b / AC-BIN-104: 入札価格 (希望価格 ± 手数料単価) と、 値域・入札制限の判定は
  // backend のプレビュー RPC の結果を出す。 入札価格が揃った時だけ問い合わせる。
  const watchedSide = watch('side')
  const watchedPrice = watch('priceYenPerKwh')
  const feePreviewInput =
    usesFeePreview &&
    isPlainDecimalInput(watchedPrice) &&
    (watchedSide === String(Side.BUY) || watchedSide === String(Side.SELL))
      ? {
          areaCode: baseline.areaCode,
          direction: Number(watchedSide) as Side,
          desiredPriceYenPerKwh: watchedPrice,
        }
      : undefined
  // 価格欄の意味 (希望価格か入札価格か) は開いた時点のまま。 応答の手数料自動考慮の ON/OFF がそれと
  // 食い違うなら (開いた後に設定が変わった)、 入力値を別の意味で保存しないよう止める。
  // 開いた時が OFF 扱いでプレビューを取れない時は、 札作成と同じく OFF の明示入力を送る (#8052)。
  const feePreview: FeeAutoConsiderPreview = useIntradayFeeAdjustedPricePreview(
    feePreviewInput,
    fee,
  )
  // AC-BIN-005c: フォーミュラの計算結果は backend のプレビュー RPC の結果を出す。
  const formula = useIntradayFormulaFields({
    isOpen: canChoosePriceType,
    deliveryDate,
    values: {
      priceType: isFormula ? 'formula' : 'fixed',
      timeCode: String(baseline.timeCode),
      side: watchedSide,
      areaCode: String(baseline.areaCode),
      formulaVariable,
      formulaReferenceArea,
      formulaOperator,
      formulaOffset,
    },
    fee,
  })
  const initialPrice = formValuesOf(
    baseline,
    feeBaseline,
    readOnly,
  ).priceYenPerKwh
  useEffect(() => {
    if (!isFeeFallbackOff) return
    // 入札価格として扱い直すので、 触っていない価格欄は保存済みの入札価格 (OFF の初期値) に戻す。
    if (getValues('priceYenPerKwh') === initialPrice) {
      setValue('priceYenPerKwh', baseline.priceYenPerKwh?.value ?? '')
    }
  }, [isFeeFallbackOff, getValues, setValue, initialPrice, baseline])
  const isFeeBlocked =
    (usesFeePreview && feePreview.kind === 'blocked') ||
    formula.submit.kind === 'feeBlocked'
  const isSaveBlocked = isBidLimitsLoading || isFeeBlocked
  useEffect(() => {
    onSaveBlockedChange(isSaveBlocked)
    return () => {
      onSaveBlockedChange(false)
    }
  }, [isSaveBlocked, onSaveBlockedChange])
  const feeHint =
    usesFeePreview || isFeeFallbackOff
      ? feeAutoConsiderHint(feePreview)
      : undefined

  return (
    <form
      id={formId}
      noValidate
      onSubmit={(e) => {
        void handleSubmit(
          (values) => {
            const volumeMw = Number(values.volumeMwh)
            const exceededQtyMax = perBidVolumeLimitExceeded(
              bidLimits,
              baseline.areaCode,
              Number(values.side) as Side,
              volumeMw,
            )
            if (exceededQtyMax !== undefined) {
              setError('volumeMwh', {
                message: t(
                  'page:biddingIntraday.form.volumeExceedsPerBidLimit',
                  {
                    max: exceededQtyMax,
                    value: volumeMw,
                  },
                ),
              })
              onInvalidSubmit(undefined)
              return
            }
            if (isFormula) {
              const formulaInput = parseIntradayFormulaFormValues({
                ...values,
                areaCode: String(baseline.areaCode),
              })
              if (formulaInput === undefined) {
                onInvalidSubmit(
                  t('page:biddingIntraday.serverError.validation'),
                )
                return
              }
              // AC-BIN-004b / AC-BIN-104: 計算結果から求めた入札価格が範囲外なら、 札作成と同じく止めて案内する。
              if (formula.submit.kind === 'outOfRange') {
                setError('formulaOffset', {
                  message: feeAdjustmentViolationMessage(
                    t,
                    formula.submit.violation,
                  ),
                })
                onInvalidSubmit(undefined)
                return
              }
              // 手数料の確認が要る間は保存ボタンを止めている (Enter での送信の二重防御)。
              if (formula.submit.kind === 'feeBlocked') return
              onValidSubmit(
                buildUpdateIntradayOrderRequest(baseline, values, {
                  kind: 'formula',
                  formula: formulaInput,
                  treatFeeAutoConsiderAsOff: isFeeFallbackOff,
                }),
              )
              return
            }
            if (usesFeePreview) {
              if (feePreview.kind === 'outOfRange') {
                setError('priceYenPerKwh', {
                  message: feeAdjustmentViolationMessage(
                    t,
                    feePreview.violation,
                  ),
                })
                onInvalidSubmit(undefined)
                return
              }
              // 確認が済んでいない間は保存ボタンを止めている (Enter での送信の二重防御)。
              if (feePreview.kind !== 'ready') return
              onValidSubmit(
                buildUpdateIntradayOrderRequest(baseline, values, {
                  kind: 'desiredPrice',
                  feeAutoConsider: feePreview.confirmed,
                }),
              )
              return
            }
            onValidSubmit(
              buildUpdateIntradayOrderRequest(baseline, values, {
                kind: 'orderPrice',
              }),
            )
          },
          (formErrors) => {
            onInvalidSubmit(
              resolveFormErrorAlertMessage(
                formErrors,
                t('page:biddingIntraday.serverError.validation'),
              ),
            )
          },
        )(e)
      }}
    >
      <chakra.fieldset margin={0} padding={0} border="none" disabled={readOnly}>
        <VStack align="stretch" gap={4}>
          {/* 売/買は入札量・入札価格と同じく下書きで変えられる (AC-BIN-007, #8279)。 */}
          <FieldRoot invalid={errors.side !== undefined} required>
            <FieldLabel>
              {t('page:biddingIntraday.form.direction')}
              <RequiredMark />
            </FieldLabel>
            <Controller
              control={control}
              name="side"
              render={({ field }) => (
                <TradeSideRadioGroup
                  ariaLabel={t('page:biddingIntraday.form.direction')}
                  isDisabled={readOnly}
                  name="intradayEdit-side"
                  onChange={field.onChange}
                  value={field.value}
                  values={{ buy: String(Side.BUY), sell: String(Side.SELL) }}
                />
              )}
            />
            <FieldErrorText>{errors.side?.message}</FieldErrorText>
          </FieldRoot>

          <HStack align="flex-start" gap={8}>
            <FieldRoot
              width="auto"
              invalid={errors.priceYenPerKwh !== undefined}
            >
              <FieldLabel htmlFor="intradayEdit-price">
                {t(
                  isDesiredPrice
                    ? 'page:biddingIntraday.form.desiredPriceYenPerKwh'
                    : 'page:biddingIntraday.form.priceYenPerKwh',
                )}
              </FieldLabel>
              {/* AC-BIN-007: 入札価格は 固定 / フォーミュラ (札作成モーダルと同じ切替)。 戦略の間は出さない。
                表示専用の札も保存済みの種別を選んだ状態で非活性に出す。 成行は価格を持たないので出さない。 */}
              {(formulaSelectable || (readOnly && !isMarketOrder)) && (
                <Box marginBottom={2}>
                  <IntradayPriceTypeRadio
                    onChange={(next) => setValue('priceType', next)}
                    readOnly={readOnly}
                    value={priceType}
                  />
                </Box>
              )}
              {!isFormula && (
                <>
                  <Input
                    {...priceReg}
                    width="120px"
                    fontSize="md"
                    textAlign="right"
                    // 成行は価格を持たない (AC-BIN-004d: 入札価格の入力欄を無効化する)。
                    disabled={isMarketOrder}
                    id="intradayEdit-price"
                    max={JEPX_PRICE_CONSTRAINT.max}
                    min={JEPX_PRICE_CONSTRAINT.min}
                    onChange={wrapNumberInputChange(
                      priceReg,
                      JEPX_PRICE_CONSTRAINT,
                    )}
                    paddingY="6px"
                    placeholder={
                      isMarketOrder
                        ? t('page:biddingIntraday.form.priceTypeMarket')
                        : undefined
                    }
                    step={JEPX_PRICE_CONSTRAINT.step}
                    type="number"
                  />
                  <FieldErrorText>
                    {errors.priceYenPerKwh?.message}
                  </FieldErrorText>
                  {/* AC-BIN-004b: 手数料自動考慮の補足表示 (入札価格・OFF の案内・取得できない時の案内)。 */}
                  {feeHint !== undefined && (
                    <Text maxWidth="320px" color={feeHint.color} fontSize="sm">
                      {t(editFeeHintKey(feeHint.key), {
                        price: feeHint.orderPriceYenPerKwh,
                      })}
                    </Text>
                  )}
                </>
              )}
            </FieldRoot>
            <FieldRoot width="auto" invalid={errors.volumeMwh !== undefined}>
              <FieldLabel htmlFor="intradayEdit-volume">
                {t('page:biddingIntraday.column.bidVolumeMw')}
              </FieldLabel>
              <Input
                {...volumeReg}
                width="120px"
                fontSize="md"
                textAlign="right"
                id="intradayEdit-volume"
                max={JEPX_VOLUME_CONSTRAINT.max}
                min={JEPX_VOLUME_CONSTRAINT.min}
                onChange={wrapNumberInputChange(
                  volumeReg,
                  JEPX_VOLUME_CONSTRAINT,
                )}
                paddingY="6px"
                step={JEPX_VOLUME_CONSTRAINT.step}
                type="number"
              />
              <FieldErrorText>{errors.volumeMwh?.message}</FieldErrorText>
            </FieldRoot>
            <Box>
              <Text color="Text.lowEmphasis" fontSize="xs">
                {t('page:biddingIntraday.column.gcDeadline')}
              </Text>
              <Text>{gcAtLabel}</Text>
            </Box>
          </HStack>

          {/* AC-BIN-005 / 005c: フォーミュラ欄。 参照エリアの既定は札のエリア。 */}
          {isFormula && (
            <FieldRoot invalid={errors.formulaOffset !== undefined}>
              <IntradayFormulaFields
                control={control}
                feeHintKey={editFeeHintKey}
                fields={formula}
                offsetError={errors.formulaOffset?.message}
                offsetInputId="intradayEdit-formulaOffset"
                register={register}
                setValue={setValue}
              />
            </FieldRoot>
          )}

          {/* AC-BIN-007: エリア・発注主体区分・JEPX受渡契約コードは読み取り専用で出す。 */}
          <HStack align="flex-start" gap={8}>
            <Box>
              <Text color="Text.lowEmphasis" fontSize="xs">
                {t('page:biddingIntraday.column.area')}
              </Text>
              <Text>{convertAreaLabel(baseline.areaCode)}</Text>
            </Box>
            <Box>
              <Text color="Text.lowEmphasis" fontSize="xs">
                {t('page:biddingIntraday.detail.issuer')}
              </Text>
              <Text>{licenseTypeLabel(baseline.issuerType)}</Text>
            </Box>
            <Box>
              <Text color="Text.lowEmphasis" fontSize="xs">
                {t('page:biddingIntraday.detail.deliveryContractCode')}
              </Text>
              <Text>{baseline.deliveryContractCd ?? '—'}</Text>
            </Box>
            {baseline.jepxBidNo !== undefined && (
              <Box>
                <Text color="Text.lowEmphasis" fontSize="xs">
                  {t('page:biddingIntraday.detail.jepxBidNo')}
                </Text>
                <Text>{baseline.jepxBidNo}</Text>
              </Box>
            )}
          </HStack>

          <FieldRoot invalid={errors.withdrawAt !== undefined}>
            <FieldLabel htmlFor="intradayEdit-withdrawAt">
              {t('page:biddingIntraday.form.withdrawAt')}
            </FieldLabel>
            <Input
              maxWidth="220px"
              id="intradayEdit-withdrawAt"
              type="datetime-local"
              {...register('withdrawAt')}
            />
            <FieldErrorText>{errors.withdrawAt?.message}</FieldErrorText>
          </FieldRoot>

          <FieldRoot invalid={errors.memoJepx !== undefined}>
            <FieldLabel htmlFor="intradayEdit-memoJepx">
              {t('page:biddingIntraday.form.memoJepx')}
            </FieldLabel>
            <TextArea
              id="intradayEdit-memoJepx"
              maxLength={70}
              rows={2}
              {...register('memoJepx')}
            />
            <FieldErrorText>{errors.memoJepx?.message}</FieldErrorText>
          </FieldRoot>

          <FieldRoot invalid={errors.memoFree !== undefined}>
            <FieldLabel htmlFor="intradayEdit-memoFree">
              {t('page:biddingIntraday.form.memoFree')}
            </FieldLabel>
            <TextArea
              id="intradayEdit-memoFree"
              maxLength={100}
              rows={2}
              {...register('memoFree')}
            />
            <FieldErrorText>{errors.memoFree?.message}</FieldErrorText>
          </FieldRoot>
        </VStack>
      </chakra.fieldset>
    </form>
  )
}
